"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformOperation = exports.convertXLSXRangeToCSV = exports.validateXlsToCSVRangeConfig = exports.validateXlsToCSVExtractValuesConfig = void 0;
const edit_1 = __importDefault(require("@sheet/edit"));
const common_1 = require("@finley-technologies/common");
const consolidateErrors_1 = require("./consolidateErrors");
const errorUtils_1 = require("./errorUtils");
const consolidateCellErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.cell, // Group by cell (that is essentially no grouping)
    (error) => ({
        sheetName: error.sheetName,
        fieldName: error.fieldName,
        message: error.message,
    }), // Map to row data
    (cell) => `cell [${cell}] had errors`);
};
const consolidateXlsx2CsvInternalErrors = (errorsWithoutField) => {
    return (0, consolidateErrors_1.consolidateErrors)(errorsWithoutField, (error) => error.message, // Group by message
    (error) => ({
        sheetName: error.sheetName,
    }), // Map to row data
    (message) => message);
};
const consolidateFieldErrors = (fieldErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(fieldErrors, (error) => error.fieldName, // Group by fieldName
    (error) => ({
        sheetName: error.sheetName,
        message: error.message,
    }), // Map to row data
    (fieldName) => `Field [${fieldName}] had errors`);
};
const consolidateToStructuredErrors = (errors) => {
    const errorsWithCell = errors.filter(
    // has cell
    (error) => !!error.cell);
    const errorsWithFields = errors.filter((error) => 
    // does not have cell but has field.
    !error.cell && !!error.fieldName);
    const otherErrors = errors.filter((error) => 
    // has neither cell nor field.
    !error.fieldName && !error.cell);
    return [
        ...consolidateCellErrors(errorsWithCell),
        ...consolidateFieldErrors(errorsWithFields),
        ...consolidateXlsx2CsvInternalErrors(otherErrors),
    ];
};
const isXlsx2CsvInternalError = (obj) => {
    return 'type' in obj && obj.type === 'xlsx2CsvInternalError';
};
const validateXlsToCSVExtractValuesConfig = (transformation) => {
    const { extract } = transformation;
    if (extract.length === 0) {
        return [
            {
                type: 'metadataValidation',
                message: 'extract must have at least one field',
            },
        ];
    }
    const uniqueFieldNames = new Set();
    const errors = [];
    extract.forEach(({ cellAddress, fieldName, sheetName }) => {
        // ensure no duplicate fieldName
        if (uniqueFieldNames.has(fieldName)) {
            errors.push({
                type: 'metadataValidation',
                message: `Field [${fieldName}] is duplicated`,
            });
        }
        else {
            uniqueFieldNames.add(fieldName);
        }
        const cellAddressDecoded = edit_1.default.utils.decode_cell(cellAddress);
        if (sheetName.length === 0) {
            errors.push({
                type: 'metadataValidation',
                message: `Field [${fieldName}] sheetName must not be empty`,
            });
        }
        if (cellAddressDecoded.c < 0 || cellAddressDecoded.r < 0) {
            errors.push({
                type: 'metadataValidation',
                message: `Field [${fieldName}] Invalid [columnIndex:${cellAddressDecoded.c}][rowIndex:${cellAddressDecoded.r}] for [cellAddress:${cellAddress}]`,
            });
        }
    });
    return errors;
};
exports.validateXlsToCSVExtractValuesConfig = validateXlsToCSVExtractValuesConfig;
const validateXlsToCSVRangeConfig = (transformation) => {
    try {
        const { headerRange, dataEndRange } = transformation;
        if (headerRange) {
            // we expect headerRange of the format A1:Z1
            // unfortunately XLSX.utils.decode_range does not throw an error
            // for single cell input like A1Z1
            (0, common_1.finleyAssert)(headerRange.includes(':'), `headerRange:${headerRange} is not valid. It must be of the format A1:Z1`);
            const decodedHeaderRange = edit_1.default.utils.decode_range(headerRange);
            const { s: start, e: end } = decodedHeaderRange;
            (0, common_1.finleyAssert)(start.r >= 0 && start.c >= 0 && end.r >= 0 && end.c >= 0, `[headerRange:${headerRange}] is not valid.`);
            (0, common_1.finleyAssert)(start.r === end.r, `[headerRange:${headerRange}] must be a single row range`);
            if (dataEndRange) {
                // we expect dataEndRange of the format A5:Z5
                // unfortunately XLSX.utils.decode_range does not throw an error
                // for single cell input like A1Z1
                (0, common_1.finleyAssert)(dataEndRange.includes(':'), `dataEndRange:${dataEndRange} is not valid. It must be of the format A5:Z5`);
                const decodedDataEndRange = edit_1.default.utils.decode_range(dataEndRange);
                const { s: dataEndRangeStart, e: dataEndRangeEnd, } = decodedDataEndRange;
                (0, common_1.finleyAssert)(dataEndRangeStart.r >= 0 &&
                    dataEndRangeStart.c >= 0 &&
                    dataEndRangeEnd.r >= 0 &&
                    dataEndRangeEnd.c >= 0, `[dataEndRange:${dataEndRange}] is not valid.`);
                (0, common_1.finleyAssert)(dataEndRangeStart.r === dataEndRangeEnd.r, `[dataEndRange:${dataEndRange}] must be a single row range`);
                (0, common_1.finleyAssert)(dataEndRangeStart.c === start.c && dataEndRangeEnd.c === end.c, `[dataEndRange:${dataEndRange}] must have same columns as [headerRange:${headerRange}]`);
                (0, common_1.finleyAssert)(dataEndRangeStart.r > start.r, `[dataEndRange:${dataEndRange}] must be after [headerRange:${headerRange}]`);
            }
        }
    }
    catch (error) {
        // caller requires that we throw an array of MetadataError.
        const errors = [];
        if (error instanceof Error) {
            errors.push({
                type: 'metadataValidation',
                message: error.message,
            });
        }
        else {
            errors.push({
                type: 'metadataValidation',
                message: 'Unexpected Error in validateXlsToCSVConfig',
            });
        }
        return errors;
    }
    return [];
};
exports.validateXlsToCSVRangeConfig = validateXlsToCSVRangeConfig;
const validateTransform = (transformation) => {
    const { mode } = transformation;
    let errors;
    if (mode === 'convertRange') {
        errors = (0, exports.validateXlsToCSVRangeConfig)(transformation);
    }
    else if (mode === 'extractValues') {
        errors = (0, exports.validateXlsToCSVExtractValuesConfig)(transformation);
    }
    else {
        return (0, common_1.finleyAssertUnreachable)(mode);
    }
    return errors.length
        ? { isValid: false, errors }
        : { isValid: true, data: null };
};
const XLSX_ASSERT_META_FIELD = 'xlsx2CsvInternalError';
const xlsxAssert = (condition, xlsx2CsvInternalError) => {
    if (condition) {
        return;
    }
    const message = `${xlsx2CsvInternalError.message}: [sheetName: ${xlsx2CsvInternalError.sheetName}], [cell: ${xlsx2CsvInternalError.cell}]`;
    const errorWithType = {
        ...xlsx2CsvInternalError,
        type: 'xlsx2CsvInternalError',
    };
    throw new common_1.error.FinleyError(message, {
        metadata: { logMeta: { [XLSX_ASSERT_META_FIELD]: errorWithType } },
    });
};
const getRangeArray = ({ start, end, }) => {
    const array = Array(end - start + 1);
    const arrayKeys = array.keys();
    const arrayWithRange = Array.from(arrayKeys);
    return arrayWithRange.map((value) => value + start);
};
const getMatchingSheet = (workbook, sheetName) => {
    const sheetNames = workbook.SheetNames;
    // sheetName is a regex, we need to find the sheet that matches the regex
    const matchingSheetNames = sheetNames.filter((name) => new RegExp(sheetName).test(name));
    // ensure that less than one sheet matches the regex
    xlsxAssert(matchingSheetNames.length === 1, {
        sheetName,
        message: `Expected 1 matching sheet [${sheetName}], found ${matchingSheetNames.length} sheets.`,
    });
    const sheet = workbook.Sheets[matchingSheetNames[0]];
    return sheet;
};
const convertXLSXRangeToCSV = ({ xlsBuffer, transformation, }) => {
    let outputBuffer;
    try {
        const { sheetName, headerRange, dataEndRange, formula } = transformation;
        const importRaw = !!transformation.importRaw;
        const workbook = edit_1.default.read(xlsBuffer, {
            type: 'buffer',
            // cellFormula: true, will get us formula along with raw and formated values
            // we always extract cellFormula, as our config may want it.
            cellFormula: true,
            // when importing raw we want dates
            // to be read as js date objects.
            cellDates: importRaw,
        });
        const sheet = getMatchingSheet(workbook, sheetName);
        const sheetRangeA1 = sheet['!ref'];
        xlsxAssert(sheetRangeA1, {
            sheetName,
            message: 'Sheet has no range',
        });
        const sheetRange = edit_1.default.utils.decode_range(sheetRangeA1);
        const { s: sheetStart, e: sheetEnd } = sheetRange;
        const effectiveHeaderRange = headerRange
            ? edit_1.default.utils.decode_range(headerRange)
            : {
                s: { c: sheetStart.c, r: sheetStart.r },
                e: { c: sheetEnd.c, r: sheetStart.r },
            };
        const { s: headerStart, e: headerEnd } = effectiveHeaderRange;
        const endRow = dataEndRange
            ? edit_1.default.utils.decode_range(dataEndRange).e.r
            : sheetEnd.r;
        // part of the sheet that we need to convert to csv starts from header row to end of sheet
        // and it uses columns specified in headerRange
        const cellRange = {
            s: { c: headerStart.c, r: headerStart.r },
            e: { c: headerEnd.c, r: endRow },
        };
        const replaceNewLines = (oldVal) => {
            const newVal = oldVal.replace(/(\r\n|\n|\r)/gm, ' ');
            return newVal;
        };
        if (formula) {
            // if formula flag is true
            // convert range to csv of formulas
            const sheetAOAWithoutNewLines = [];
            const rowRange = getRangeArray({ start: headerStart.r, end: endRow });
            const columnRange = getRangeArray({
                start: headerStart.c,
                end: headerEnd.c,
            });
            rowRange.forEach((rowNum) => {
                const row = [];
                columnRange.forEach((colNum) => {
                    const nextCell = sheet[edit_1.default.utils.encode_cell({ r: rowNum, c: colNum })];
                    if (typeof nextCell === 'undefined') {
                        row.push(undefined);
                    }
                    else if (rowNum === headerStart.r) {
                        const headerValue = replaceNewLines(nextCell.v ? `${nextCell.v}` : `${nextCell.w}`);
                        row.push(headerValue);
                    }
                    else {
                        const formulaValue = nextCell.f ? `${nextCell.f}` : `${nextCell.v}`;
                        row.push(formulaValue);
                    }
                });
                sheetAOAWithoutNewLines.push(row);
            });
            // convert aoa back to sheet
            const targetSheet = edit_1.default.utils.aoa_to_sheet(sheetAOAWithoutNewLines);
            const csv = edit_1.default.utils.sheet_to_csv(targetSheet);
            outputBuffer = common_1.finleyBuffer.fromString(csv);
        }
        else {
            // convert range to json.
            // we do range => json => sheet => csv
            // because sheet_to_csv does not work on specific range.
            // this also allows better control over formatting in the sheet.
            // `raw: true` is only available in sheet_to_json
            // this also allows us remove newline from headers.
            const json = edit_1.default.utils
                .sheet_to_json(sheet, {
                range: cellRange,
                // exclude blank rows.
                blankrows: false,
                // when importRaw is true, we get raw values from cells,
                // except for dates which are returned as js date objects.
                raw: importRaw,
            })
                .map((row) => {
                if (!importRaw)
                    return row; // If not importing raw, return the row as is
                // when importRaw is true, we get raw values from cells,
                // except for dates which are returned as js date objects.
                // we need to format them to date.
                const newRow = {};
                Object.entries(row).forEach(([key, value]) => {
                    newRow[key] =
                        // @ts-ignore
                        value instanceof Date
                            ? common_1.date.dateToUTCFullDateString(value)
                            : value;
                });
                return newRow;
            });
            // xlsx allows new line characters in cells
            // but csv utilities dont work well with new line characters in header row.
            // lets remove new lines from headers.
            const jsonWithoutNewLines = json.map((row) => {
                const newRow = {};
                Object.keys(row).forEach((key) => {
                    const newKey = replaceNewLines(key);
                    newRow[newKey] = row[key];
                });
                return newRow;
            });
            // convert json back to sheet
            const targetSheet = edit_1.default.utils.json_to_sheet(jsonWithoutNewLines);
            const csv = edit_1.default.utils.sheet_to_csv(targetSheet);
            outputBuffer = common_1.finleyBuffer.fromString(csv);
        }
    }
    catch (err) {
        if (err instanceof common_1.error.FinleyError) {
            const xlsx2CsvInternalError = err.metadata?.logMeta?.[XLSX_ASSERT_META_FIELD];
            if (isXlsx2CsvInternalError(xlsx2CsvInternalError)) {
                return { isValid: false, errors: [xlsx2CsvInternalError] };
            }
        }
        throw err;
    }
    return { isValid: true, data: { outputBuffer }, supplementals: [] };
};
exports.convertXLSXRangeToCSV = convertXLSXRangeToCSV;
/**
 * Managing supplemental outputs for "xlsx2csv:extractValues" operation
 *
 * 1. "template_extract_overlay": supplemental xlsx file that overlays the extract config
 * on top of provided file (acting as source template). This was part of a request to help
 * the identification process of any template <> extract config deviations (i.e., moved cells).
 * Users can simply search for "FINLEY_" to highlight the configured cells
 */
const _supplementalExtractXlsxValues = (params) => {
    const { xlsBuffer, transformation } = params;
    const results = [];
    const templateWorkbook = edit_1.default.read(xlsBuffer, {
        type: 'buffer',
        cellStyles: true,
        cellFormula: true,
        dense: true,
    });
    const templateExtractOverlayErrors = [];
    // Manage "template_extract_overlay" XLSX supplemental file
    for (const { sheetName, fieldName, cellAddress } of transformation.extract) {
        const targetWorksheet = templateWorkbook.Sheets[sheetName];
        if (!targetWorksheet) {
            templateExtractOverlayErrors.push(`[Sheet:${sheetName}] not found`);
            break;
        }
        // Add value with prefix "FINLEY_" so it'll be easy to find ("cmd + f")
        edit_1.default.utils.sheet_add_aoa(targetWorksheet, [[{ v: `FINLEY_${fieldName}` }]], { origin: cellAddress });
        // Update styling to be fairly highlighted and bold for easier identification
        edit_1.default.utils.sheet_set_range_style(targetWorksheet, cellAddress, {
            z: 'General',
            alignment: { horizontal: 'center' },
            bold: true,
            color: { rgb: common_1.consts.COLOR_HEX_CODES.RED },
            bgColor: { rgb: common_1.consts.COLOR_HEX_CODES.YELLOW },
            top: { style: 'medium' },
            right: { style: 'medium' },
            bottom: { style: 'medium' },
            left: { style: 'medium' },
        });
    }
    let outputBuffer = null;
    if (templateExtractOverlayErrors.length === 0) {
        outputBuffer = edit_1.default.write(templateWorkbook, {
            bookType: 'xlsx',
            type: 'array',
            compression: true,
            cellStyles: true,
            cellDates: true,
        });
    }
    results.push({
        operation: 'xlsx2csv',
        name: 'template_extract_overlay.xlsx',
        outputBuffer,
        errors: templateExtractOverlayErrors,
    });
    return results;
};
const extractXLSXValues = ({ xlsBuffer, transformation, }) => {
    const { extract } = transformation;
    const workbook = edit_1.default.read(xlsBuffer, { type: 'buffer', cellFormula: true });
    const data = {};
    const errors = [];
    extract.forEach(({ sheetName, cellAddress: cellAddressA1, fieldName, importRaw }) => {
        try {
            const errorTemplate = { sheetName, cell: cellAddressA1, fieldName };
            const sheet = getMatchingSheet(workbook, sheetName);
            const sheetRangeA1 = sheet['!ref'];
            xlsxAssert(sheetRangeA1, {
                ...errorTemplate,
                message: 'Sheet has no range',
            });
            const sheetRange = edit_1.default.utils.decode_range(sheetRangeA1);
            // validate that the address is withing sheet's range.
            const cellAddress = edit_1.default.utils.decode_cell(cellAddressA1);
            xlsxAssert(cellAddress.c >= 0 &&
                cellAddress.r >= 0 &&
                cellAddress.c <= sheetRange.e.c &&
                cellAddress.r <= sheetRange.e.r, {
                ...errorTemplate,
                message: `Cell address '${cellAddressA1}' is outside sheet range.`,
            });
            const cellValue = sheet[cellAddressA1];
            xlsxAssert(cellValue, {
                ...errorTemplate,
                message: 'Cell is empty',
            });
            // SheetJS does not evaluate formulas on its own. It simply extracts the precalculated result from the file.
            // If there is no precalculated result in the file, cellValue.w will NOT have correct value.
            // https://github.com/SheetJS/sheetjs/issues/481
            // This can happen when the Excel file was saved programmatically (like servicer report generated by Finley),
            // in those cases precalculated result won't be in the file. The file needs to be opened in Excel and saved manually.
            // before using it as input to this function. We need to figure out that file is not precalculated and throw an error.
            data[fieldName] = importRaw ? cellValue.v : cellValue.w;
        }
        catch (err) {
            if (err instanceof common_1.error.FinleyError) {
                const xlsx2CsvInternalError = err.metadata?.logMeta?.[XLSX_ASSERT_META_FIELD];
                if (isXlsx2CsvInternalError(xlsx2CsvInternalError)) {
                    errors.push(xlsx2CsvInternalError);
                    return; // continue in forEach loop
                }
            }
            throw err;
        }
    });
    if (errors.length) {
        return { isValid: false, errors };
    }
    const supplementals = _supplementalExtractXlsxValues({
        xlsBuffer,
        transformation,
    });
    return {
        isValid: true,
        data: {
            outputBuffer: common_1.csvConversion.csvArrayToFinleyBuffer({
                csvArray: [data],
                newline: '\n', // arrayToCsvBuffer uses \r\n by default
            }),
        },
        supplementals,
    };
};
const performTransform = async ({ dataTapeBuffers, transformation, }) => {
    try {
        (0, common_1.finleyAssert)(dataTapeBuffers.length === 1, 'Expected 1 data tape');
        const xlsBuffer = dataTapeBuffers[0];
        let result;
        const { mode } = transformation;
        switch (mode) {
            case 'convertRange':
                result = (0, exports.convertXLSXRangeToCSV)({
                    xlsBuffer,
                    transformation,
                });
                break;
            case 'extractValues':
                result = extractXLSXValues({
                    xlsBuffer,
                    transformation,
                });
                break;
            default:
                return (0, common_1.finleyAssertUnreachable)(mode);
        }
        if (result.isValid) {
            return result;
        }
        return {
            isValid: false,
            errors: consolidateToStructuredErrors(result.errors),
        };
    }
    catch (error) {
        return (0, errorUtils_1.convertErrorToTapeFailureResult)(error, 'xlsx2csv: ');
    }
};
exports.transformOperation = {
    operation: 'xlsx2csv',
    validateTransform,
    performTransform,
};
//# sourceMappingURL=xlsx2csv.js.map